/**
 * GlobalSearch — the shell topbar's customer/dog typeahead (spec C2,
 * 2026-07-09 admin shell wave).
 *
 * Counter Test: from ANY page, a front-desk worker with a wet dog on a
 * leash types two letters of the DOG's name and lands on that dog's card
 * in one Enter — or two letters of the owner's name and lands on the
 * owner. No navigating to /customers first, and no extra hop once there.
 *
 * Destination rule (#751b — see `matchedDogId`): the typed query decides.
 * A query matching exactly one of the row's dogs goes to `/dogs/:dogId`;
 * anything else (owner match, ambiguous multi-dog match, a server too old
 * to send dog ids, or a role that cannot open /dogs) goes to the customer
 * profile.
 *
 * Design philosophy compliance:
 *  - Principle 2 (obvious next action): a result row goes to the thing it
 *    was searched for, not one screen away from it.
 *  - Principle 4 (feels fast): 250ms debounce, skeleton rows while
 *    loading — never a blank or spinner-only popover.
 *  - Five states: idle (affordance only), keep-typing hint (<2 chars),
 *    loading (skeleton), empty (teaching copy + "browse all" action),
 *    error (what happened + retry). Stale doesn't apply — a typeahead
 *    result can't outlive its own popover.
 *  - Tokens only; no raw colors in the CSS this component ships with.
 *
 * Data source: the EXISTING admin CRM search (`fetchCustomers` →
 * GET /api/admin/crm/customers?search=…) — the same client fn the
 * Customers page uses. Deliberately NO new server endpoint.
 *
 * Keyboard contract (spec C2 AC):
 *  - `/` focuses the field from anywhere (unless a text-entry element
 *    already has focus); Cmd/Ctrl-K always focuses it.
 *  - ArrowUp/ArrowDown walk the results (aria-activedescendant), Enter
 *    opens the active result.
 *  - Escape closes the popover; a second Escape leaves the field and
 *    returns focus to wherever it was before the shortcut grabbed it.
 */
import {
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { fetchCustomers, type CustomerSummary } from '../lib/api.js';
import { isTextEntryTarget } from '../lib/keyboard.js';
import { useAuth } from '../contexts/AuthContext.js';
import { effectiveRolesOf } from '../lib/rbac-matrix.js';
import { rolesCanAccessPath } from '../lib/route-permissions.js';
import { Icon } from './icons.js';
import { DogAvatar } from './dog-first/index.js';

const DEBOUNCE_MS = 250;
const MIN_CHARS = 2;
const RESULT_LIMIT = 8;

/**
 * #751b — which dog (if any) did this search actually ask for?
 *
 * Kylie searches a PET name because she has that pet in front of her, so a
 * dog-led result must land on the dog's card. The row itself cannot answer
 * "which dog" on its own: the server aggregates dogs `ORDER BY d.name`, so
 * `dogs[0]` is the alphabetically first one, not the matched one — routing
 * there would send a search for "Biscuit" on the Apollo+Biscuit household to
 * Apollo, trading one wrong destination for another.
 *
 * So: the query decides. Exactly one name match → that dog. Zero matches
 * (she typed a phone number or the owner's name) → she asked for the owner.
 * Two or more → the row is genuinely ambiguous and the owner profile, which
 * lists every pet, is the honest answer rather than a coin flip.
 *
 * Returns null whenever the customer route is the right destination —
 * including when the server is too old to send `dog_refs` at all.
 */
function matchedDogId(
  customer: CustomerSummary,
  query: string,
): string | null {
  const needle = query.trim().toLowerCase();
  if (needle.length === 0) return null;
  const refs = customer.dog_refs ?? [];
  const hits = refs.filter((d) => d.name.toLowerCase().includes(needle));
  const only = hits.length === 1 ? hits[0] : undefined;
  return only && only.id.length > 0 ? only.id : null;
}

type SearchStatus =
  | 'idle' // empty field — the affordance is the state
  | 'minChars' // 1 char typed — teach, don't fetch
  | 'loading' // debounce elapsed, request in flight (skeleton)
  | 'results' // ≥1 match
  | 'empty' // 0 matches — teaching copy + browse-all action
  | 'error'; // request failed — situation + recovery, no tech words

type GlobalSearchProps = {
  apiUrl: string;
};

export function GlobalSearch({ apiUrl }: GlobalSearchProps): JSX.Element {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { staff, token } = useAuth();

  const [query, setQuery] = useState('');
  const [status, setStatus] = useState<SearchStatus>('idle');
  const [results, setResults] = useState<CustomerSummary[]>([]);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [popoverOpen, setPopoverOpen] = useState(false);
  // <720px: the field lives behind a topbar icon (spec C1 mobile row).
  const [mobileOpen, setMobileOpen] = useState(false);

  const rootRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  /** Where focus came from when a shortcut grabbed it (Escape returns it). */
  const previousFocusRef = useRef<HTMLElement | null>(null);
  /** Monotonic request sequence — a stale response never overwrites a newer one. */
  const seqRef = useRef(0);

  const listboxId = useId();
  const optionId = (index: number): string => `${listboxId}-option-${index}`;

  const closePopover = useCallback((): void => {
    setPopoverOpen(false);
    setActiveIndex(-1);
  }, []);

  const runSearch = useCallback(
    async (term: string): Promise<void> => {
      if (!token) return;
      const seq = ++seqRef.current;
      setStatus('loading');
      setPopoverOpen(true);
      try {
        const res = await fetchCustomers(apiUrl, token, {
          search: term,
          limit: RESULT_LIMIT,
        });
        if (seqRef.current !== seq) return; // a newer query superseded us
        setResults(res.customers);
        setActiveIndex(res.customers.length > 0 ? 0 : -1);
        setStatus(res.customers.length > 0 ? 'results' : 'empty');
        setPopoverOpen(true);
      } catch {
        if (seqRef.current !== seq) return;
        setResults([]);
        setActiveIndex(-1);
        setStatus('error');
        setPopoverOpen(true);
      }
    },
    [apiUrl, token],
  );

  // Debounced fetch — exactly one request per typed burst (spec C2 AC).
  useEffect(() => {
    const term = query.trim();
    if (term.length === 0) {
      seqRef.current += 1; // invalidate any in-flight response
      setStatus('idle');
      setResults([]);
      setActiveIndex(-1);
      setPopoverOpen(false);
      return undefined;
    }
    if (term.length < MIN_CHARS) {
      seqRef.current += 1;
      setStatus('minChars');
      setResults([]);
      setActiveIndex(-1);
      setPopoverOpen(true);
      return undefined;
    }
    const handle = window.setTimeout(() => {
      void runSearch(term);
    }, DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query, runSearch]);

  // Global focus shortcuts: `/` (guarded against text entry) and
  // Cmd/Ctrl-K (modifier makes intent unambiguous, so no guard).
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent): void => {
      const slash =
        e.key === '/' && !e.metaKey && !e.ctrlKey && !e.altKey;
      const cmdK =
        (e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey);
      if (!slash && !cmdK) return;
      if (slash && isTextEntryTarget(e.target)) return;
      e.preventDefault();
      previousFocusRef.current =
        document.activeElement instanceof HTMLElement
          ? document.activeElement
          : null;
      setMobileOpen(true);
      // The mobile field may have been display-gated this frame; focus
      // after the state flush.
      window.requestAnimationFrame(() => inputRef.current?.focus());
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, []);

  // Click-away closes the popover (and the mobile overlay).
  useEffect(() => {
    if (!popoverOpen && !mobileOpen) return undefined;
    const onPointerDown = (e: MouseEvent | TouchEvent): void => {
      if (rootRef.current && e.target instanceof Node) {
        if (rootRef.current.contains(e.target)) return;
      }
      closePopover();
      setMobileOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
    };
  }, [popoverOpen, mobileOpen, closePopover]);

  const leaveField = useCallback((): void => {
    closePopover();
    setMobileOpen(false);
    inputRef.current?.blur();
    const prev = previousFocusRef.current;
    previousFocusRef.current = null;
    prev?.focus();
  }, [closePopover]);

  /**
   * #751b — one place decides the destination, so the mouse and the keyboard
   * can never diverge (before this, Enter and click both went to the customer
   * because there was only one handler; the fix must keep them identical).
   */
  const selectResult = useCallback(
    (customer: CustomerSummary): void => {
      setQuery('');
      setResults([]);
      setStatus('idle');
      closePopover();
      setMobileOpen(false);

      const dogId = matchedDogId(customer, query);
      // A dog card the caller's role cannot open is a RouteGuard "Access
      // restricted" screen — a worse dead end than the owner profile they
      // used to get. The accountant can search (customers:read:all) and holds
      // no pets:read at all, so this is a real path, not a hypothetical.
      const dogRoute = dogId ? `/dogs/${dogId}` : null;
      if (
        dogRoute &&
        rolesCanAccessPath(effectiveRolesOf(staff), dogRoute)
      ) {
        navigate(dogRoute);
        return;
      }
      navigate(`/customers/${customer.id}`);
    },
    [closePopover, navigate, query, staff],
  );

  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
  ): void => {
    if (e.key === 'Escape') {
      e.preventDefault();
      if (popoverOpen) {
        closePopover();
        return;
      }
      leaveField();
      return;
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      if (status !== 'results' || results.length === 0) return;
      e.preventDefault();
      setPopoverOpen(true);
      setActiveIndex((prev) => {
        const delta = e.key === 'ArrowDown' ? 1 : -1;
        const next = prev + delta;
        if (next < 0) return results.length - 1;
        if (next >= results.length) return 0;
        return next;
      });
      return;
    }
    if (e.key === 'Enter') {
      if (status !== 'results' || results.length === 0) return;
      e.preventDefault();
      const target = results[activeIndex >= 0 ? activeIndex : 0];
      if (target) selectResult(target);
    }
  };

  const showListbox = popoverOpen && status === 'results';

  return (
    <div
      ref={rootRef}
      className={`ferro-global-search${
        mobileOpen ? ' ferro-global-search--mobile-open' : ''
      }`}
    >
      {/* <720px affordance: the topbar shows a search icon; tapping it
          expands the field across the topbar row (spec C1). */}
      <button
        type="button"
        className="ferro-global-search__toggle"
        aria-label={t('admin.shell.search.open')}
        onClick={() => {
          previousFocusRef.current =
            document.activeElement instanceof HTMLElement
              ? document.activeElement
              : null;
          setMobileOpen(true);
          window.requestAnimationFrame(() => inputRef.current?.focus());
        }}
      >
        <Icon name="search" />
      </button>

      <div className="ferro-global-search__field">
        <Icon name="search" className="ferro-global-search__field-icon" />
        <input
          ref={inputRef}
          type="text"
          className="ferro-input ferro-global-search__input"
          role="combobox"
          aria-expanded={showListbox}
          aria-controls={listboxId}
          aria-activedescendant={
            showListbox && activeIndex >= 0 ? optionId(activeIndex) : undefined
          }
          aria-autocomplete="list"
          aria-label={t('admin.shell.search.label')}
          placeholder={t('admin.shell.search.placeholder')}
          autoComplete="off"
          autoCorrect="off"
          spellCheck={false}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onKeyDown={handleInputKeyDown}
          onFocus={() => {
            if (query.trim().length > 0) setPopoverOpen(true);
          }}
        />
        <kbd className="ferro-global-search__kbd" aria-hidden="true">
          /
        </kbd>
        <button
          type="button"
          className="ferro-global-search__cancel"
          aria-label={t('admin.shell.search.close')}
          onClick={leaveField}
        >
          <Icon name="close" />
        </button>
      </div>

      {popoverOpen && status !== 'idle' ? (
        <div className="ferro-global-search__popover">
          {status === 'minChars' ? (
            <p className="ferro-global-search__hint" role="status">
              {t('admin.shell.search.minChars')}
            </p>
          ) : null}

          {status === 'loading' ? (
            <div
              className="ferro-global-search__state"
              role="status"
              aria-label={t('admin.shell.search.loading')}
            >
              <div className="ferro-skeleton ferro-global-search__skeleton-row" />
              <div className="ferro-skeleton ferro-global-search__skeleton-row" />
              <div className="ferro-skeleton ferro-global-search__skeleton-row" />
            </div>
          ) : null}

          {status === 'empty' ? (
            <div className="ferro-global-search__state" role="status">
              <p className="ferro-global-search__empty-title">
                {t('admin.shell.search.empty', { query: query.trim() })}
              </p>
              <p className="ferro-global-search__hint">
                {t('admin.shell.search.emptyHint')}
              </p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-global-search__action"
                onClick={() => {
                  closePopover();
                  setMobileOpen(false);
                  setQuery('');
                  navigate('/customers');
                }}
              >
                {t('admin.shell.search.viewAll')}
              </button>
            </div>
          ) : null}

          {status === 'error' ? (
            <div className="ferro-global-search__state" role="alert">
              <p className="ferro-global-search__empty-title">
                {t('admin.shell.search.error')}
              </p>
              <button
                type="button"
                className="ferro-btn ferro-btn--ghost ferro-global-search__action"
                onClick={() => void runSearch(query.trim())}
              >
                {t('admin.shell.search.retry')}
              </button>
            </div>
          ) : null}

          <div
            id={listboxId}
            role="listbox"
            aria-label={t('admin.shell.search.resultsLabel')}
            className="ferro-global-search__listbox"
            hidden={!showListbox}
          >
            {results.map((customer, index) => {
              const dogs = customer.dogs ?? [];
              return (
                <div
                  key={customer.id}
                  id={optionId(index)}
                  role="option"
                  aria-selected={index === activeIndex}
                  tabIndex={-1}
                  className={`ferro-global-search__option${
                    index === activeIndex
                      ? ' ferro-global-search__option--active'
                      : ''
                  }`}
                  // mousedown (not click) so selection wins the race against
                  // the click-away close + input blur.
                  onMouseDown={(e) => {
                    e.preventDefault();
                    selectResult(customer);
                  }}
                  onMouseEnter={() => setActiveIndex(index)}
                >
                  {/* #545 — dog-first: staff search by PET name. Lead with an
                      initial-letter avatar + the dog name(s); owner + phone
                      are secondary. Owner-led fallback when the customer has
                      no dogs on file. (The typeahead payload carries dog
                      NAMES only — photo/breed live on the customer detail.) */}
                  {dogs.length > 0 ? (
                    <>
                      <DogAvatar name={dogs[0] ?? ''} photoUrl={null} size="sm" />
                      <span className="ferro-global-search__option-body">
                        <span className="ferro-global-search__option-name ferro-global-search__option-name--dog">
                          {dogs.join(', ')}
                        </span>
                        <span className="ferro-global-search__option-meta">
                          {customer.name} · {customer.phone}
                        </span>
                      </span>
                    </>
                  ) : (
                    <span className="ferro-global-search__option-body">
                      <span className="ferro-global-search__option-name">
                        {customer.name}
                      </span>
                      <span className="ferro-global-search__option-meta">
                        {customer.phone}
                      </span>
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ) : null}
    </div>
  );
}
